import React, { useEffect, useState } from "react";
import axios from "axios";
import API_URL from "../config";
//import cloudinary from '../cloudinary-config/cloudinary.js';  
// ── Size presets by category + productType ────────────────────────────────────
const SIZE_PRESETS = {
  women: {
    clothing:  ["XS", "S", "M", "L", "XL", "XXL"],
    shoes:     ["EU 35", "EU 36", "EU 37", "EU 38", "EU 39", "EU 40", "EU 41", "EU 42"],
    accessory: [],   // accessories usually don't need sizes
  },
  kids: {
    clothing:  ["2Y", "3Y", "4Y", "5Y", "6Y", "7Y", "8Y", "9Y", "10Y", "11Y", "12Y"],
    shoes:     ["EU 20", "EU 22", "EU 24", "EU 26", "EU 28", "EU 30", "EU 32", "EU 34"],
    accessory: [],
  },
  cosmetics: {
    cosmetics: ["15ml", "30ml", "50ml", "75ml", "100ml", "150ml", "200ml"],
  },
};

// Derive which productType options are shown per category
const TYPE_OPTIONS = {
  women:     ["clothing", "shoes", "accessory"],
  kids:      ["clothing", "shoes", "accessory"],
  cosmetics: ["cosmetics"],
};

const EMPTY_FORM = {
  name:        "",
  price:       "",
  image:       null,
  category:    "",
  productType: "clothing",
  description: "",
  isNewArrival:       false,
  sizes:       [],
  colors:      [],     // [{ name, hex }]
};

export default function Admin() {
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [products,   setProducts]   = useState([]);
  const [editId,     setEditId]     = useState(null);
  const [colorInput, setColorInput] = useState({ name: "", hex: "#e8b4c0" });
  const [saving,     setSaving]     = useState(false);

  const token = localStorage.getItem("token");

  // ── Auth check ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) window.location.href = "/login";
  }, [token]);

  // ── Fetch products ──────────────────────────────────────────────────────────
  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products`);
      setProducts(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const handleLogout = () => {
    localStorage.removeItem("token"); // Clears the session
    window.location.href = "/login";  // Redirects back to your login page
  };

  // ── When category changes, auto-set productType and clear sizes ─────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;

    if (name === "category") {
      const defaultType = TYPE_OPTIONS[value]?.[0] || "clothing";
      setForm(f => ({ ...f, category: value, productType: defaultType, sizes: [] }));
    } else if (name === "productType") {
      setForm(f => ({ ...f, productType: value, sizes: [] }));
    } else {
      setForm(f => ({ ...f, [name]: val }));
    }
  };

  // ── File input ──────────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    setForm(f => ({ ...f, image: e.target.files[0] }));
  };

  // ── Toggle a size chip ──────────────────────────────────────────────────────
  const toggleSize = (size) => {
    setForm(f => ({
      ...f,
      sizes: f.sizes.includes(size)
        ? f.sizes.filter(s => s !== size)
        : [...f.sizes, size]
    }));
  };

  // ── Colors ──────────────────────────────────────────────────────────────────
  const addColor = () => {
    if (!colorInput.name.trim()) return;
    if (form.colors.find(c => c.name.toLowerCase() === colorInput.name.toLowerCase())) return;
    setForm(f => ({ ...f, colors: [...f.colors, { ...colorInput }] }));
    setColorInput({ name: "", hex: "#e8b4c0" });
  };

  const removeColor = (name) => {
    setForm(f => ({ ...f, colors: f.colors.filter(c => c.name !== name) }));
  };

  // ── Submit (add or update) ──────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = new FormData();
      data.append("name",        form.name);
      data.append("price",       form.price);
      data.append("category",    form.category);
      data.append("productType", form.productType);
      data.append("description", form.description || "");
      data.append("isNewArrival",       form.isNewArrival);
      // Arrays/objects must be JSON-stringified in FormData
      data.append("sizes",  JSON.stringify(form.sizes));
      data.append("colors", JSON.stringify(form.colors || []));;

      if (form.image) data.append("image", form.image);

      const headers = { Authorization: token };

      if (editId) {
        await axios.put(`${API_URL}/api/products/${editId}`, data, { headers });
        setEditId(null);
        
      } else {
        await axios.post(`${API_URL}/api/products`          , data, { headers });
      }

      setForm(EMPTY_FORM);
      setColorInput({ name: "", hex: "#e8b4c0" });
      fetchProducts();
      alert("Saved ✅");
    } catch (err) {
      console.log(err.response?.data || err.message);
      alert("Failed ❌");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API_URL}/api/products/${id}`, {
        headers: { Authorization: token }
      });
      fetchProducts();
    } catch {
      alert("Delete failed");
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────────────
  const editProduct = (p) => {
    setForm({
      name:        p.name,
      price:       p.price,
      image:       null,
      category:    p.category,
      productType: p.productType || TYPE_OPTIONS[p.category]?.[0] || "clothing",
      description: p.description || "",
      isNewArrival:       p.isNewArrival || false,
      sizes:       p.sizes  || [],
      colors:      p.colors || [],
    });
    setEditId(p._id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Sizes available for current selection ───────────────────────────────────
  const availableSizes = SIZE_PRESETS[form.category]?.[form.productType] || [];
  const typeOptions    = TYPE_OPTIONS[form.category] || [];

  // ── UI ──────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{adminStyles}</style>
      <div className="adm-root">

        <div className="adm-header">
          <p className="adm-eyebrow">STMZ Group</p>
          <h1 className="adm-title">Admin Panel</h1>
        </div>

        <button onClick={handleLogout} className="adm-logout-btn">
            Log Out
          </button>

        {/* ── FORM ── */}
        <section className="adm-card">
          <h2 className="adm-card-title">
            {editId ? "✏️ Edit Product" : "＋ Add New Product"}
          </h2>

          <form onSubmit={handleSubmit} className="adm-form">

            {/* Row 1 — name + price */}
            <div className="adm-row-2">
              <div className="adm-field">
                <label className="adm-label">Product Name</label>
                <input name="name" value={form.name} onChange={handleChange}
                  className="adm-input" placeholder="e.g. Silk Midi Dress" required />
              </div>
              <div className="adm-field">
                <label className="adm-label">Price (AED)</label>
                <input name="price" value={form.price} onChange={handleChange}
                  className="adm-input" placeholder="e.g. 299" type="number" required />
              
              </div>
              
            </div>

            {/* Row 2 — category + productType */}
            <div className="adm-row-2">
              <div className="adm-field">
                <label className="adm-label">Category</label>
                <select name="category" value={form.category} onChange={handleChange}
                  className="adm-input" required>
                  <option value="">Select category</option>
                  <option value="women">Women</option>
                  <option value="kids">Kids</option>
                  <option value="cosmetics">Cosmetics</option>
                </select>
              </div>

              {form.category && typeOptions.length > 1 && (
                <div className="adm-field">
                  <label className="adm-label">Product Type</label>
                  <select name="productType" value={form.productType}
                    onChange={handleChange} className="adm-input">
                    {typeOptions.map(t => (
                      <option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="adm-field">
              <label className="adm-label">Description</label>
              <textarea name="description" value={form.description} onChange={handleChange}
                className="adm-input adm-textarea"
                placeholder="Describe the product — fabric, fit, occasion…" />
            </div>

            {/* Image + isNew */}
            <div className="adm-row-2">
              <div className="adm-field">
                <label className="adm-label">Product Image</label>
                <label className="adm-file-label">
                  <input type="file" accept="image/*" onChange={handleFileChange}
                    className="adm-file-input" />
                  <span className="adm-file-btn">
                    {form.image ? `📎 ${form.image.name}` : "Choose image…"}
                  </span>
                </label>
                {editId && !form.image && (
                  <p className="adm-hint">Leave empty to keep existing image</p>
                )}
              </div>
              <div className="adm-field adm-center">
                <label className="adm-label">Mark as New Arrival?</label>
                <label className="adm-toggle">
                  <input type="checkbox" name="isNewArrival" checked={form.isNewArrival}
                    onChange={handleChange} />
                  <span className="adm-toggle-track">
                    <span className="adm-toggle-thumb" />
                  </span>
                  <span className="adm-toggle-text">
                    {form.isNewArrival ? "Yes — show badge" : "No"}
                  </span>
                </label>
              </div>
            </div>

            {/* ── SIZES ── */}
            {availableSizes.length > 0 && (
              <div className="adm-field">
                <label className="adm-label">
                  Sizes Available
                  <span className="adm-hint-inline"> — tick all that apply</span>
                </label>
                <div className="adm-size-grid">
                  {availableSizes.map(size => (
                    <button
                      type="button"
                      key={size}
                      onClick={() => toggleSize(size)}
                      className={`adm-size-chip ${form.sizes.includes(size) ? "active" : ""}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                {form.sizes.length > 0 && (
                  <p className="adm-hint">Selected: {form.sizes.join(", ")}</p>
                )}
              </div>
            )}

            {/* No sizes for accessory */}
            {form.category && availableSizes.length === 0 && form.productType === "accessory" && (
              <p className="adm-hint" style={{ marginTop: 4 }}>
                Accessories don't require sizes — leave blank.
              </p>
            )}

            {/* ── COLORS ── */}
            <div className="adm-field">
              <label className="adm-label">
                Colours Available
                <span className="adm-hint-inline"> — add each colour separately</span>
              </label>

              <div className="adm-color-row">
                <input
                  value={colorInput.name}
                  onChange={e => setColorInput(c => ({ ...c, name: e.target.value }))}
                  className="adm-input adm-color-name-input"
                  placeholder="Colour name (e.g. Rose)"
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addColor())}
                />
                <div className="adm-color-picker-wrap">
                  <input
                    type="color"
                    value={colorInput.hex}
                    onChange={e => setColorInput(c => ({ ...c, hex: e.target.value }))}
                    className="adm-color-picker"
                    title="Pick colour"
                  />
                  <span className="adm-color-hex">{colorInput.hex}</span>
                </div>
                <button type="button" onClick={addColor} className="adm-add-color-btn">
                  Add
                </button>
              </div>

              {/* Color swatches already added */}
              {form.colors.length > 0 && (
                <div className="adm-swatches">
                  {form.colors.map(c => (
                    <div key={c.name} className="adm-swatch-item">
                      <span className="adm-swatch-dot" style={{ background: c.hex }} />
                      <span className="adm-swatch-name">{c.name}</span>
                      <button type="button" onClick={() => removeColor(c.name)}
                        className="adm-swatch-remove">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="adm-form-actions">
              {editId && (
                <button type="button" className="adm-cancel-btn"
                  onClick={() => { setForm(EMPTY_FORM); setEditId(null); }}>
                  Cancel
                </button>
              )}
              <button type="submit" className="adm-submit-btn" disabled={saving}>
                {saving ? "Saving…" : editId ? "Update Product" : "Add Product"}
              </button>
            </div>

          </form>
        </section>

        {/* ── PRODUCT LIST ── */}
        <section>
          <h2 className="adm-card-title" style={{ marginBottom: 20 }}>
            All Products ({products.length})
          </h2>

          <div className="adm-products-grid">
            {products.map(p => (
              <div key={p._id} className="adm-product-card">

                <div className="adm-product-img-wrap">
                  <img src={p.image} alt={p.name} className="adm-product-img" />
                  {p.isNewArrival && <span className="adm-new-badge">New</span>}
                </div>

                <div className="adm-product-body">
                  <p className="adm-product-cat">{p.category} · {p.productType || "clothing"}</p>
                  <p className="adm-product-name">{p.name}</p>
                  <p className="adm-product-price">{p.price} AED</p>
                   
                  <p style={{ fontSize: '12px', color: '#7a6e68', marginTop: '4px' }}>{p.description}</p>

                  {/* Sizes preview */}
                  {p.sizes?.length > 0 && (
                    <div className="adm-product-tags">
                      {p.sizes.map(s => (
                        <span key={s} className="adm-tag">{s}</span>
                      ))}
                    </div>
                  )}

                 {/* Colors preview
{p.colors?.length > 0 && (
  <div className="adm-product-colors">
    {p.colors.map(c => (
      <span key={c.name} className="adm-color-dot"
        style={{ backgroundColor: c.hex }} title={c.name}>
        {/* If the dot is invisible, this text will still show up */}
        {/* {!c.hex && "No Hex!"} 
      </span>
    ))}
    {/* This text below will tell us if data exists at all */}
    {/* <span style={{fontSize: '10px'}}>{p.colors.length} colors found</span>
//   </div>
)} */} *//

                  {/* Colors preview with visible names */}
{p.colors?.length > 0 && (
  <div className="adm-product-colors" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    {p.colors.map(c => (
      <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span 
          className="adm-color-dot" 
          style={{ background: c.hex, display: 'inline-block' }} 
        />
        <span style={{ fontSize: '11px', color: '#7a6e68', textTransform: 'capitalize' }}>
          {c.name}
        </span>
      </div>
    ))}
  </div>
)}

                  <div className="adm-product-actions">
                    <button onClick={() => editProduct(p)} className="adm-edit-btn">Edit</button>
                    <button onClick={() => deleteProduct(p._id)} className="adm-delete-btn">Delete</button>
                  </div>
                </div>

              </div>
            ))}
          </div>
        </section>

      </div>
    </>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const adminStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&family=DM+Sans:wght@300;400;500&display=swap');

  .adm-root { font-family:'DM Sans',sans-serif; background:#fdf9f6; min-height:100vh; padding:40px; max-width:1100px; margin:0 auto; }
  @media(max-width:680px){ .adm-root{ padding:20px; } }

  .adm-header { margin-bottom:36px; }
  .adm-eyebrow { font-size:10px; font-weight:500; letter-spacing:.26em; text-transform:uppercase; color:#c87090; margin-bottom:8px; }
  .adm-title { font-family:'Cormorant Garamond',serif; font-size:40px; font-weight:300; color:#1a1410; margin:0; }

 /* Add this inside your adminStyles variable */
  .adm-logout-btn {
    padding: 8px 16px;
    background: transparent;
    color: #7a6e68;
    border: 1px solid #ddd8d2;
    font-size: 12px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    letter-spacing: .1em;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.2s;
  }
  .adm-logout-btn:hover {
    border-color: #1a1410;
    color: #1a1410;
  }

  /* CARD */
  .adm-card { background:#fff; border:1px solid #ede8e2; padding:32px; margin-bottom:48px; }
  .adm-card-title { font-family:'Cormorant Garamond',serif; font-size:22px; font-weight:400; color:#1a1410; margin:0 0 28px; }

  /* FORM */
  .adm-form { display:flex; flex-direction:column; gap:22px; }
  .adm-row-2 { display:grid; grid-template-columns:1fr 1fr; gap:18px; }
  @media(max-width:580px){ .adm-row-2{ grid-template-columns:1fr; } }
  .adm-field { display:flex; flex-direction:column; gap:7px; }
  .adm-label { font-size:11px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; color:#7a6e68; }
  .adm-hint-inline { font-size:10px; letter-spacing:.06em; color:#b0a89e; text-transform:none; font-weight:400; }
  .adm-hint { font-size:11px; color:#a09890; font-style:italic; margin-top:5px; }

  .adm-input {
    border:1.5px solid #ddd8d2; background:#fdf9f6;
    padding:10px 13px; font-size:13.5px; font-family:'DM Sans',sans-serif;
    color:#1a1410; outline:none; transition:border-color .18s;
    width:100%; box-sizing:border-box;
  }
  .adm-input:focus { border-color:#c87090; }
  .adm-textarea { min-height:90px; resize:vertical; }

  select.adm-input { cursor:pointer; appearance:none;
    background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23a09890' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat:no-repeat; background-position:right 12px center; padding-right:34px; }

  /* FILE */
  .adm-file-label { cursor:pointer; }
  .adm-file-input { display:none; }
  .adm-file-btn { display:block; border:1.5px dashed #ddd8d2; padding:12px; font-size:13px; color:#a09890; text-align:center; transition:border-color .18s,color .18s; }
  .adm-file-btn:hover { border-color:#c87090; color:#c87090; }

  /* TOGGLE */
  .adm-center { justify-content:flex-start; }
  .adm-toggle { display:flex; align-items:center; gap:12px; cursor:pointer; margin-top:4px; }
  .adm-toggle input { display:none; }
  .adm-toggle-track { width:42px; height:24px; background:#ddd8d2; border-radius:12px; position:relative; transition:background .2s; flex-shrink:0; }
  .adm-toggle input:checked ~ .adm-toggle-track { background:#c87090; }
  .adm-toggle-thumb { position:absolute; top:3px; left:3px; width:18px; height:18px; background:white; border-radius:50%; transition:transform .2s; box-shadow:0 1px 3px rgba(0,0,0,.15); }
  .adm-toggle input:checked ~ .adm-toggle-track .adm-toggle-thumb { transform:translateX(18px); }
  .adm-toggle-text { font-size:13px; color:#7a6e68; }

  /* SIZE CHIPS */
  .adm-size-grid { display:flex; flex-wrap:wrap; gap:8px; }
  .adm-size-chip { padding:7px 14px; border:1.5px solid #ddd8d2; background:transparent; font-size:12px; font-weight:500; font-family:'DM Sans',sans-serif; color:#7a6e68; cursor:pointer; transition:all .18s; letter-spacing:.05em; }
  .adm-size-chip:hover { border-color:#1a1410; color:#1a1410; }
  .adm-size-chip.active { background:#1a1410; color:#fdf9f6; border-color:#1a1410; }

  /* COLORS */
  .adm-color-row { display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
  .adm-color-name-input { flex:1; min-width:160px; }
  .adm-color-picker-wrap { display:flex; align-items:center; gap:8px; border:1.5px solid #ddd8d2; padding:6px 12px; background:#fdf9f6; }
  .adm-color-picker { width:30px; height:30px; border:none; background:none; cursor:pointer; padding:0; border-radius:50%; overflow:hidden; }
  .adm-color-hex { font-size:12px; color:#a09890; font-family:monospace; }
  .adm-add-color-btn { padding:10px 22px; background:#1a1410; color:#fdf9f6; border:none; font-size:12px; font-weight:500; font-family:'DM Sans',sans-serif; letter-spacing:.14em; text-transform:uppercase; cursor:pointer; white-space:nowrap; transition:background .18s; }
  .adm-add-color-btn:hover { background:#c87090; }

  .adm-swatches { display:flex; flex-wrap:wrap; gap:8px; margin-top:10px; }
  .adm-swatch-item { display:flex; align-items:center; gap:7px; background:#f4efe9; border:1px solid #ede8e2; padding:5px 10px 5px 8px; }
  .adm-swatch-dot { width:16px; height:16px; border-radius:50%; flex-shrink:0; border:1px solid rgba(0,0,0,.08); }
  .adm-swatch-name { font-size:12px; color:#1a1410; }
  .adm-swatch-remove { background:none; border:none; color:#c87090; cursor:pointer; font-size:16px; line-height:1; padding:0 0 0 4px; transition:color .15s; }
  .adm-swatch-remove:hover { color:#1a1410; }

  /* FORM ACTIONS */
  .adm-form-actions { display:flex; gap:12px; justify-content:flex-end; padding-top:8px; }
  .adm-submit-btn { padding:12px 36px; background:#1a1410; color:#fdf9f6; border:none; font-size:12px; font-weight:500; font-family:'DM Sans',sans-serif; letter-spacing:.18em; text-transform:uppercase; cursor:pointer; transition:background .2s; }
  .adm-submit-btn:hover { background:#c87090; }
  .adm-submit-btn:disabled { opacity:.5; cursor:not-allowed; }
  .adm-cancel-btn { padding:12px 24px; background:transparent; color:#7a6e68; border:1.5px solid #ddd8d2; font-size:12px; font-weight:500; font-family:'DM Sans',sans-serif; letter-spacing:.18em; text-transform:uppercase; cursor:pointer; transition:all .18s; }
  .adm-cancel-btn:hover { border-color:#1a1410; color:#1a1410; }

  /* PRODUCTS GRID */
  .adm-products-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; }
  @media(max-width:860px){ .adm-products-grid{ grid-template-columns:repeat(2,1fr); } }
  @media(max-width:480px){ .adm-products-grid{ grid-template-columns:1fr; } }

  .adm-product-card { background:#fff; border:1px solid #ede8e2; overflow:hidden; }
  .adm-product-img-wrap { position:relative; background:#f0ebe5; }
  .adm-product-img { width:100%; height:160px; object-fit:cover; display:block; }
  .adm-new-badge { position:absolute; top:10px; left:10px; background:#1a1410; color:#fdf9f6; font-size:9px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; padding:4px 9px; }
  .adm-product-body { padding:14px; display:flex; flex-direction:column; gap:6px; }
  .adm-product-cat { font-size:10px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; color:#c87090; }
  .adm-product-name { font-family:'Cormorant Garamond',serif; font-size:17px; font-weight:400; color:#1a1410; }
  .adm-product-price { font-size:13px; font-weight:500; color:#7a6e68; }

  .adm-product-tags { display:flex; flex-wrap:wrap; gap:5px; margin-top:2px; }
  .adm-tag { font-size:10px; background:#f4efe9; color:#7a6e68; padding:3px 8px; letter-spacing:.06em; }

  .adm-product-colors { display:flex; gap:6px; flex-wrap:wrap; margin-top:2px; }
  .adm-color-dot { width:14px; height:14px; border-radius:50%; border:1.5px solid rgba(0,0,0,.1); display:inline-block; flex-shrink: 0;}
   
  .adm-product-actions { display:flex; gap:8px; margin-top:8px; }
  .adm-edit-btn { flex:1; padding:7px; background:#1a1410; color:#fdf9f6; border:none; font-size:11px; font-weight:500; font-family:'DM Sans',sans-serif; letter-spacing:.12em; text-transform:uppercase; cursor:pointer; transition:background .18s; }
  .adm-edit-btn:hover { background:#c87090; }
  .adm-delete-btn { flex:1; padding:7px; background:transparent; color:#c87090; border:1.5px solid #f5dde4; font-size:11px; font-weight:500; font-family:'DM Sans',sans-serif; letter-spacing:.12em; text-transform:uppercase; cursor:pointer; transition:all .18s; }
  .adm-delete-btn:hover { background:#c87090; color:#fff; border-color:#c87090; }
`;